import type { FastifyReply } from 'fastify'

/**
 * Standardized API response helper
 * Ensures all error responses follow the same format: { error: string, statusCode: number, timestamp: string }
 */
export class ApiResponse {
  /**
   * Send error response with standard format
   */
  static error(reply: FastifyReply, statusCode: number, message: string) {
    return reply.status(statusCode).send({
      error: message,
      statusCode,
      timestamp: new Date().toISOString()
    })
  }

  /**
   * Send 400 Bad Request error
   */
  static badRequest(reply: FastifyReply, message = 'Bad Request') {
    return this.error(reply, 400, message)
  }

  /**
   * Send 401 Unauthorized error
   */
  static unauthorized(reply: FastifyReply, message = 'Unauthorized') {
    return this.error(reply, 401, message)
  }

  /**
   * Send 403 Forbidden error
   */
  static forbidden(reply: FastifyReply, message = 'Forbidden') {
    return this.error(reply, 403, message)
  }

  /**
   * Send 404 Not Found error
   */
  static notFound(reply: FastifyReply, message = 'Not Found') {
    return this.error(reply, 404, message)
  }

  /**
   * Send 429 Too Many Requests error
   */
  static tooManyRequests(reply: FastifyReply, message = 'Too Many Requests') {
    return this.error(reply, 429, message)
  }

  /**
   * Send 500 Internal Server Error
   */
  static internalError(reply: FastifyReply, message = 'Internal Server Error') {
    return this.error(reply, 500, message)
  }

  /**
   * Send success response with data
   */
  static success<T = any>(reply: FastifyReply, data: T, statusCode = 200) {
    return reply.status(statusCode).send(data)
  }

  /**
   * Send 204 No Content
   */
  static noContent(reply: FastifyReply) {
    return reply.status(204).send()
  }
}
